import {
  type Dispatch,
  type SetStateAction,
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { useGetAgentSessionSupplierDiscoveryItemsQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import {
  getTableCacheKey,
  mergeSupplierItems,
  normalizeSupplierItem,
  resolveTableItemsUpdate,
} from 'utils/AICreatorHelper';

import type ISupplierDiscoveryTableItem from '../interfaces/ISupplierDiscoveryTableItem';
import type IUseAICreatorSupplierTableParams from '../interfaces/IUseAICreatorSupplierTableParams';

const useAICreatorSupplierTable = ({
  valueChainMapId,
  sessionId,
  unitId,
  listContextKey,
  open,
}: IUseAICreatorSupplierTableParams) => {
  const [offset, setOffset] = useState(0);
  const [tableItems, setTableItems] = useState<ISupplierDiscoveryTableItem[]>([]);
  const [refreshToken, setRefreshToken] = useState(0);
  const lastAppliedDataKeyRef = useRef<string>('');
  const tableItemsRef = useRef<ISupplierDiscoveryTableItem[]>([]);
  const tableItemsCacheRef = useRef<Record<string, ISupplierDiscoveryTableItem[]>>({});
  const activeTableCacheKeyRef = useRef<string>('');
  const staleTableCacheKeysRef = useRef<Set<string>>(new Set());
  const staleRefetchStartedAtByCacheKeyRef = useRef<Map<string, number>>(new Map());
  const pendingActiveRefetchRef = useRef(false);
  const offsetRef = useRef(offset);

  const activeTableCacheKey = useMemo(
    () => getTableCacheKey(valueChainMapId, sessionId, unitId, listContextKey),
    [listContextKey, sessionId, unitId, valueChainMapId],
  );

  const {
    currentData: agentSessionSupplierItems,
    isFetching: isAgentSessionSupplierItemsFetching,
    refetch: refetchAgentSessionSupplierItems,
    fulfilledTimeStamp,
  } = useGetAgentSessionSupplierDiscoveryItemsQuery({
    valueChainMapId: valueChainMapId || '',
    sessionId: sessionId || 0,
    unitId: unitId || 0,
    offset,
    limit: DATA_SET_PAGINATION_COUNT,
  }, {
    skip: !sessionId || !valueChainMapId || !unitId,
  });

  const canRefetchAgentSessionSupplierItems = Boolean(sessionId && valueChainMapId && unitId);
  const canRefetchAgentSessionSupplierItemsRef = useRef(canRefetchAgentSessionSupplierItems);
  const refetchAgentSessionSupplierItemsRef = useRef(refetchAgentSessionSupplierItems);
  const { meta, items } = agentSessionSupplierItems?.data || {};
  const hasMore = meta?.hasNext || false;

  const onLoadMore = useCallback(() => {
    if (hasMore) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  }, [hasMore]);

  const setTableItemsForCacheKey = useCallback((
    cacheKey: string,
    updater: SetStateAction<ISupplierDiscoveryTableItem[]>,
  ) => {
    const previousItems = tableItemsCacheRef.current[cacheKey]
      || (activeTableCacheKeyRef.current === cacheKey ? tableItemsRef.current : []);
    const nextItems = resolveTableItemsUpdate(previousItems, updater);

    if (cacheKey) {
      tableItemsCacheRef.current[cacheKey] = nextItems;
    }

    if (activeTableCacheKeyRef.current === cacheKey) {
      tableItemsRef.current = nextItems;
      setTableItems(nextItems);
    }
  }, []);

  const setActiveTableItems = useCallback<Dispatch<SetStateAction<ISupplierDiscoveryTableItem[]>>>((updater) => {
    setTableItemsForCacheKey(activeTableCacheKeyRef.current, updater);
  }, [setTableItemsForCacheKey]);

  const setTableItemsForContext = useCallback((
    contextUnitId: number,
    contextListKey: string,
    updater: SetStateAction<ISupplierDiscoveryTableItem[]>,
  ) => {
    const cacheKey = getTableCacheKey(valueChainMapId, sessionId, contextUnitId, contextListKey);

    setTableItemsForCacheKey(cacheKey, updater);
  }, [sessionId, setTableItemsForCacheKey, valueChainMapId]);

  const refreshActiveStaleTable = useCallback((cacheKey: string) => {
    tableItemsRef.current = [];
    setTableItems([]);
    lastAppliedDataKeyRef.current = '';
    staleRefetchStartedAtByCacheKeyRef.current.set(cacheKey, Date.now());
    setRefreshToken((prev) => prev + 1);

    if (offsetRef.current === 0) {
      pendingActiveRefetchRef.current = false;

      if (canRefetchAgentSessionSupplierItemsRef.current) {
        refetchAgentSessionSupplierItemsRef.current();
      }

      return;
    }

    pendingActiveRefetchRef.current = true;
    setOffset(0);
  }, []);

  const markTableItemsContextStale = useCallback((contextUnitId: number, contextListKey: string) => {
    const cacheKey = getTableCacheKey(valueChainMapId, sessionId, contextUnitId, contextListKey);

    if (!cacheKey) {
      return;
    }

    staleTableCacheKeysRef.current.add(cacheKey);
    staleRefetchStartedAtByCacheKeyRef.current.delete(cacheKey);
    delete tableItemsCacheRef.current[cacheKey];

    if (activeTableCacheKeyRef.current !== cacheKey) {
      return;
    }

    staleTableCacheKeysRef.current.delete(cacheKey);
    refreshActiveStaleTable(cacheKey);
  }, [refreshActiveStaleTable, sessionId, valueChainMapId]);

  const handleRefreshAgentSessionSupplierItems = useCallback(() => {
    if (!canRefetchAgentSessionSupplierItems) {
      return;
    }

    setRefreshToken((prev) => prev + 1);

    if (offset === 0) {
      refetchAgentSessionSupplierItems();
      return;
    }

    setOffset(0);
  }, [canRefetchAgentSessionSupplierItems, offset, refetchAgentSessionSupplierItems]);

  useEffect(() => {
    offsetRef.current = offset;
    canRefetchAgentSessionSupplierItemsRef.current = canRefetchAgentSessionSupplierItems;
    refetchAgentSessionSupplierItemsRef.current = refetchAgentSessionSupplierItems;
  }, [canRefetchAgentSessionSupplierItems, offset, refetchAgentSessionSupplierItems]);

  useEffect(() => {
    if (!pendingActiveRefetchRef.current || offset !== 0) {
      return;
    }

    pendingActiveRefetchRef.current = false;

    if (canRefetchAgentSessionSupplierItems) {
      refetchAgentSessionSupplierItems();
    }
  }, [canRefetchAgentSessionSupplierItems, offset, refetchAgentSessionSupplierItems]);

  useEffect(() => {
    tableItemsRef.current = tableItems;
  }, [tableItems]);

  useEffect(() => {
    activeTableCacheKeyRef.current = activeTableCacheKey;
  }, [activeTableCacheKey]);

  useEffect(() => {
    if (open) {
      return;
    }

    tableItemsCacheRef.current = {};
    staleTableCacheKeysRef.current.clear();
    staleRefetchStartedAtByCacheKeyRef.current.clear();
    pendingActiveRefetchRef.current = false;
    setOffset(0);
    setTableItems([]);
    tableItemsRef.current = [];
    setRefreshToken(0);
    lastAppliedDataKeyRef.current = '';
  }, [open]);

  useEffect(() => {
    tableItemsCacheRef.current = {};
    staleTableCacheKeysRef.current.clear();
    staleRefetchStartedAtByCacheKeyRef.current.clear();
    pendingActiveRefetchRef.current = false;
    setOffset(0);
    setTableItems([]);
    tableItemsRef.current = [];
    setRefreshToken(0);
    lastAppliedDataKeyRef.current = '';
  }, [sessionId, valueChainMapId]);

  useEffect(() => {
    setOffset(0);
    setRefreshToken(0);
    lastAppliedDataKeyRef.current = '';

    if (!open || !activeTableCacheKey) {
      setTableItems([]);
      tableItemsRef.current = [];
      return;
    }

    if (staleTableCacheKeysRef.current.has(activeTableCacheKey)) {
      staleTableCacheKeysRef.current.delete(activeTableCacheKey);
      delete tableItemsCacheRef.current[activeTableCacheKey];
      refreshActiveStaleTable(activeTableCacheKey);
      return;
    }

    const cachedItems = tableItemsCacheRef.current[activeTableCacheKey] || [];

    setTableItems(cachedItems);
    tableItemsRef.current = cachedItems;
  }, [activeTableCacheKey, open, refreshActiveStaleTable]);

  useEffect(() => {
    if (!open || !unitId || !items) {
      return;
    }

    const dataKey = [
      sessionId || 0,
      listContextKey,
      unitId,
      offset,
      refreshToken,
      fulfilledTimeStamp || 0,
    ].join('-');

    if (lastAppliedDataKeyRef.current === dataKey) {
      return;
    }

    const normalizedItems = items.map((item) => normalizeSupplierItem(
      item as ISupplierDiscoveryTableItem,
    ));
    const staleRefetchStartedAt = staleRefetchStartedAtByCacheKeyRef.current.get(activeTableCacheKey);

    if (
      staleRefetchStartedAt !== undefined
      && (!fulfilledTimeStamp || fulfilledTimeStamp < staleRefetchStartedAt)
    ) {
      return;
    }

    if (offset === 0) {
      if (staleRefetchStartedAt !== undefined) {
        setTableItemsForCacheKey(activeTableCacheKey, normalizedItems);
        staleRefetchStartedAtByCacheKeyRef.current.delete(activeTableCacheKey);
      } else {
        setTableItemsForCacheKey(
          activeTableCacheKey,
          (prev) => mergeSupplierItems(normalizedItems, prev),
        );
      }

      lastAppliedDataKeyRef.current = dataKey;
      return;
    }

    setTableItemsForCacheKey(activeTableCacheKey, (prev) => {
      const existingIds = new Set(prev.map((item) => item.id));
      const newItems = normalizedItems.filter((item) => !existingIds.has(item.id));

      return [...prev, ...newItems];
    });
    lastAppliedDataKeyRef.current = dataKey;
  }, [
    activeTableCacheKey,
    fulfilledTimeStamp,
    items,
    listContextKey,
    offset,
    open,
    refreshToken,
    sessionId,
    setTableItemsForCacheKey,
    unitId,
  ]);

  return {
    tableItems,
    setTableItems: setActiveTableItems,
    setTableItemsForContext,
    markTableItemsContextStale,
    onLoadMore,
    hasMore,
    isAgentSessionSupplierItemsFetching,
    refetchAgentSessionSupplierItems: handleRefreshAgentSessionSupplierItems,
  };
};

export default useAICreatorSupplierTable;
